
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import {
  fetchTeamMembers,
  createTeamMember as createTeamMemberQuery,
  updateTeamMember as updateTeamMemberQuery,
  deleteTeamMember as deleteTeamMemberQuery,
  pauseTeamMember as pauseTeamMemberQuery,
  resumeTeamMember as resumeTeamMemberQuery,
  type TeamMember
} from '@/lib/queries/teamMembers';

// Re-export TeamMember type for use in other modules
export type { TeamMember };

export const useTeamMembers = (tenantId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['team-members', tenantId],
    queryFn: async () => {
      if (!tenantId || !user) return [];
      return fetchTeamMembers(tenantId);
    },
    enabled: !!tenantId && !!user,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  const teamMembers = data || [];

  const createTeamMember = async (teamMember: Omit<TeamMember, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      const data = await createTeamMemberQuery(tenantId, teamMember);

      // Update cache optimistically
      queryClient.setQueryData(['team-members', tenantId], (old: TeamMember[] | undefined) => {
        return [data, ...(old || [])];
      });

      return { data, error: null as any };
    } catch (error) {
      console.error('Error creating team member:', error);
      return { data: null, error };
    }
  };

  const updateTeamMember = async (id: string, updates: Partial<Omit<TeamMember, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>>) => {
    try {
      const data = await updateTeamMemberQuery(id, updates);

      // Update cache optimistically
      queryClient.setQueryData(['team-members', tenantId], (old: TeamMember[] | undefined) => {
        return (old || []).map(tm => tm.id === id ? data : tm);
      });

      return { data, error: null as any };
    } catch (error) {
      console.error('Error updating team member:', error);
      return { data: null, error };
    }
  };

  const deleteTeamMember = async (id: string) => {
    // Let errors propagate so callers can handle them appropriately
    await deleteTeamMemberQuery(id);

    // Update cache optimistically
    queryClient.setQueryData(['team-members', tenantId], (old: TeamMember[] | undefined) => {
      return (old || []).filter(tm => tm.id !== id);
    });
  };

  const pauseTeamMember = async (id: string, until?: string | null, reason?: string | null) => {
    await pauseTeamMemberQuery(id, until, reason);
    // paused_at is set server-side (now()), so refetch rather than guess the value.
    await queryClient.invalidateQueries({ queryKey: ['team-members', tenantId] });
  };

  const resumeTeamMember = async (id: string) => {
    await resumeTeamMemberQuery(id);
    await queryClient.invalidateQueries({ queryKey: ['team-members', tenantId] });
  };

  return {
    teamMembers,
    isLoading,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    pauseTeamMember,
    resumeTeamMember,
    refetch,
  };
};
